import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { EmberRead, KeySentence } from '../api';
import { provenanceLine, shortDate, splitParagraphs } from '../format';
import { EmberMediaHeader } from './EmberMediaHeader';
import { EmberPhotoRow, photosUnder } from './EmberPhotos';
import { Link } from './Link';
import { PeopleLine } from '../tree/PeopleLine';
import './EmberBody.css';

/**
 * The Ember itself, read (2026-09-12 拍板: one Ember page, wherever it opens).
 * One sheet of paper (2026-09-19 拍板) — the cover card in the thread,
 * opened: cover and music to the sheet's edge, the receipt — the day and the
 * door it was told through — the title, the theme it began from when there
 * was one, the text, and the door to the original conversation as the
 * sheet's last row. The owner's page at /me/embers/:id and the page read
 * over the chat (§4.5) both draw this; what differs is what stands around
 * the sheet — management under it on the owner's page, a way back to the
 * chat and the door to that management in the bar on the other.
 *
 * The key sentences (§6.3, 2026-09-15 拍板): the 1–3 sentences the writer
 * named as carrying the piece stand in the prose under a marker wash, the
 * way a reader marks a page. A tap on one opens, under its paragraph, the
 * speaker's own words it rests on — `In your words` — since whoever reads
 * this page is the one who told it. One open at a time; the same tap closes
 * it. An Ember with none reads exactly as before.
 *
 * The speaker's photos (2026-09-15 拍板): the ones the writer placed stand
 * under their paragraph (EmberPhotos), whole; a tap opens the set.
 */
export function EmberBody({ ember, transcriptHref = null, onTalkAbout = null, logSeen }: {
  /** The Ember as its owner reads it — passed through from whichever face fetched it. */
  ember: EmberRead;
  /** The owner-only original conversation (§4.13); absent → no door. */
  transcriptHref?: string | null;
  /**
   * The owner's door into the conversation about this Ember (2026-09-24 拍板,
   * §4.5): "Have thoughts about this?" — the page opens the typed conversation
   * with a first sentence in the field and the Ember travelling with that
   * turn (talk-about.ts). Absent → no door.
   */
  onTalkAbout?: (() => void) | null;
  /** `ui.seen` name for the header entering view, when this page counts one. */
  logSeen?: string;
}): ReactElement {
  const paragraphs = splitParagraphs(ember.canonical_text);
  const receipt = provenanceLine(ember.provenance);
  const title = ember.title || 'Untitled Ember';
  const keys = ember.key_sentences.filter((k) => k && typeof k.sentence === 'string' && k.sentence.length > 0);
  const photos = ember.photos;
  // Which key sentence's words are open, by its index in `keys`; null = none.
  const [openKey, setOpenKey] = useState<number | null>(null);
  return (
    <div className="ember-body-sheet">
      <EmberMediaHeader media={ember.media} alt={`${title} — illustration`} logEmberId={ember.id} />
      <header className="ember-body-head" data-log-seen={logSeen} data-log-ember-id={logSeen ? ember.id : undefined}>
        {/* The receipt readers get (§0.9), on the owner's own page — the day
            this was told and how they told it, standing where a diary entry
            carries its date (2026-09-04 拍板). Absent only on rows whose
            source conversation is gone. */}
        {receipt && <p className="ember-body-receipt">{receipt}</p>}
        <h1 className="ember-body-title" dir="auto">{title}</h1>
      </header>
      <article className="ember-body-text">
        {paragraphs.map((p, i) => {
          const pieces = markKeySentences(p.text, keys);
          const openHere = pieces.find((piece) => piece.key === openKey);
          return (
            <div className="ember-body-block" key={i}>
              <p className="ember-body-para" dir="auto">
                {pieces.map((piece, j) => (piece.key === null ? piece.text : (
                  <mark
                    key={j}
                    className={`ember-key${piece.key === openKey ? ' is-open' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-expanded={piece.key === openKey}
                    aria-label="Show the words this rests on"
                    data-log="ember_key_open"
                    data-log-state={piece.key === openKey ? 'close' : 'open'}
                    data-log-ember-id={ember.id}
                    onClick={() => setOpenKey(piece.key === openKey ? null : piece.key)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      setOpenKey(piece.key === openKey ? null : piece.key);
                    }}
                  >{piece.text}</mark>
                )))}
              </p>
              {openHere && openHere.key !== null && keys[openHere.key].rests_on && (
                <aside className="ember-key-words" dir="auto">
                  <span className="ember-key-words-label">In your words</span>
                  <q>{keys[openHere.key].rests_on}</q>
                </aside>
              )}
              <EmberPhotoRow photos={photosUnder(photos, p)} all={photos} emberId={ember.id} />
            </div>
          );
        })}
      </article>
      {/* The telling this was written from (§4.13, 2026-09-04). The Ember is
          the finished thing; this is the evening it came out of, kept for the
          owner to read back. Owner-only — it is not a second, unedited version
          of the Ember for anyone else. */}
      {/* The lights this Ember stands at (§4.17, 2026-09-24 拍板; 2026-09-26
          拍板 the list): the one thing about other people that needs nobody's
          permission — how many stood where this person stands, and the page
          where what they went through is said in sentences true of two or
          more of them. One row per light that has answered questions, each
          led by the tree's lamp and carrying the question its page opens
          with; nothing when there is none. Nobody is named, nothing of
          theirs is shown here. */}
      {ember.lights.length > 0 && (
        <section className="ember-body-lights" aria-labelledby={`ember-lights-${ember.id}`}>
          <h2 className="ember-body-lights-title" id={`ember-lights-${ember.id}`}>Others have stood here</h2>
          <p className="ember-body-lights-sub">Read what they went through. You can ask a question there.</p>
          <ul className="ember-body-lights-list">
            {ember.lights.map((light) => (
              <li key={light.slug}>
                <Link
                  to={`/places/${encodeURIComponent(light.slug)}`}
                  className="ember-body-light"
                  data-log="ember_light_open"
                  data-log-ember-id={ember.id}
                  data-log-state="ember_body"
                >
                  <span className="ember-body-light-lamp" aria-hidden />
                  <span className="ember-body-light-body">
                    <span className="ember-body-light-name">{light.title}</span>
                    <span className="ember-body-light-meta"><PeopleLine count={light.people_count} /></span>
                    {light.question && <span className="ember-body-light-q">{light.question}</span>}
                  </span>
                  <svg className="ember-body-light-chevron" viewBox="0 0 24 24" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {/* The pages of this Ember's path (§4.2, 2026-10-02 拍板): the other
          pages of the same thing, as this person's own cover cards. Nothing
          when there are fewer than two. */}
      {ember.path_pages.length >= 2 && <PathPages ember={ember} />}
      {onTalkAbout && (
        <button
          type="button"
          className="ember-body-transcript-door ember-body-talk-door"
          data-log="ember_talk_about"
          data-log-ember-id={ember.id}
          onClick={onTalkAbout}
        >
          <svg className="ember-body-door-glyph" viewBox="0 0 24 24" aria-hidden>
            <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4.5 4v-4A2.5 2.5 0 0 1 4 13.5z" />
          </svg>
          <span>Have thoughts about this?<span className="ember-body-door-sub">Talk it over with Ember</span></span>
          <svg className="ember-body-door-chevron" viewBox="0 0 24 24" aria-hidden>
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      )}
      {transcriptHref && (
        <Link
          to={transcriptHref}
          className="ember-body-transcript-door"
          data-log="ember_transcript_open"
          data-log-ember-id={ember.id}
        >
          <svg className="ember-body-door-glyph" viewBox="0 0 24 24" aria-hidden>
            <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h7A2.5 2.5 0 0 1 16 6.5v4a2.5 2.5 0 0 1-2.5 2.5H9l-3.5 3v-3A2.5 2.5 0 0 1 4 10.5z" />
            <path d="M18.5 9.5h.5a2 2 0 0 1 2 2v3.5a2 2 0 0 1-2 2h-.5v2.5l-3-2.5h-3" />
          </svg>
          <span>Read the original conversation</span>
          <svg className="ember-body-door-chevron" viewBox="0 0 24 24" aria-hidden>
            <path d="M9 6l6 6-6 6" />
          </svg>
        </Link>
      )}
    </div>
  );
}

/** The strip's side gutter, the sheet's own (EmberBody.css .ember-body-pages-strip). */
const PAGES_GUTTER = 22;

/**
 * The pages of this Ember's path (§4.2, 2026-10-02 拍板): every page of the
 * same thing this person told, as the cover cards they already know from the
 * chat and the notebook, in telling order, this page among them wearing the
 * amber ring the card wore when it landed. Title and day only — the page
 * says nothing about what the thing is or how long it has run. Two and a
 * half cards across, a gutter apart; the strip scrolls sideways, every card
 * (the last one too) can stand at the left gutter, it opens with the page
 * before this one standing there and this page second (so where it came
 * from and what followed are both in view — this page alone at the gutter,
 * when it is the newest, left the strip looking like one card and nothing
 * to swipe to; the first page itself stands at the gutter), and a
 * paper-coloured fade at each edge says there is more on that side (left
 * once it has moved, right until the end); one dot
 * per page under it, the amber dot the card aligned at the left as the strip
 * is swiped (this page is told apart by its ring on the card, not by a dot).
 */
function PathPages({ ember }: { ember: EmberRead }): ReactElement {
  const strip = useRef<HTMLDivElement | null>(null);
  // Where the strip stands: at its start, at its end, and which card is the
  // one aligned at the left (the dot that moves as the strip is swiped).
  const [pos, setPos] = useState({ start: true, end: false, at: 0 });
  useEffect(() => {
    const el = strip.current;
    if (!el) return undefined;
    const cards = Array.from(el.querySelectorAll<HTMLElement>('.ember-body-page'));
    const here = cards.findIndex((card) => card.classList.contains('is-this'));
    const first = cards[Math.max(0, here - 1)];
    if (first) el.scrollLeft = first.offsetLeft - PAGES_GUTTER;
    const check = () => {
      let at = 0;
      cards.forEach((card, i) => {
        if (Math.abs(card.offsetLeft - PAGES_GUTTER - el.scrollLeft) < Math.abs(cards[at].offsetLeft - PAGES_GUTTER - el.scrollLeft)) at = i;
      });
      setPos({ start: el.scrollLeft <= 4, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4, at });
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => { el.removeEventListener('scroll', check); window.removeEventListener('resize', check); };
  }, [ember.id]);
  return (
    <section className="ember-body-pages" aria-labelledby={`ember-pages-${ember.id}`}>
      <h2 className="ember-body-pages-title" id={`ember-pages-${ember.id}`}>More pages of this</h2>
      <p className="ember-body-pages-sub">You came back to it on other days.</p>
      <div className={`ember-body-pages-strip-wrap${pos.start ? ' is-start' : ''}${pos.end ? ' is-end' : ''}`}>
        <div className="ember-body-pages-strip" ref={strip} data-log-seen="ember_paths_strip" data-log-ember-id={ember.id}>
          {ember.path_pages.map((p) => {
            const here = p.id === ember.id;
            const day = shortDate(p.told_at);
            const title = p.title || 'Untitled Ember';
            const inner = (
              <>
                <span className={`ember-body-page-cover${p.cover_url ? '' : ' is-paper'}`} aria-hidden>
                  {p.cover_url ? <img src={p.cover_url} alt="" loading="lazy" /> : <span>{title}</span>}
                </span>
                <span className="ember-body-page-title">{title}</span>
                <span className="ember-body-page-day">{here ? `This page${day ? ` · ${day}` : ''}` : day}</span>
              </>
            );
            return here
              ? <div key={p.id} className="ember-body-page is-this" aria-current="page">{inner}</div>
              : (
                <Link
                  key={p.id}
                  to={`/me/embers/${encodeURIComponent(p.id)}`}
                  className="ember-body-page"
                  data-log="ember_path_page_open"
                  data-log-ember-id={ember.id}
                >{inner}</Link>
              );
          })}
        </div>
      </div>
      {/* One dot per page; the amber one is the card aligned at the left as
          the strip is swiped (the carousel habit). */}
      <div className="ember-body-pages-dots" aria-hidden>
        {ember.path_pages.map((p, i) => <i key={p.id} className={i === pos.at ? 'is-at' : undefined} />)}
      </div>
    </section>
  );
}

type Piece = { text: string; key: number | null };

/**
 * Cut one paragraph into plain runs and key-sentence runs. Each key sentence
 * is found verbatim (edge kept only the ones that appear word for word in the
 * text); overlaps keep the earlier match. A sentence that appears in no
 * paragraph is simply not drawn.
 */
function markKeySentences(paragraph: string, keys: KeySentence[]): Piece[] {
  const hits: { start: number; end: number; key: number }[] = [];
  keys.forEach((k, key) => {
    let from = 0;
    for (;;) {
      const at = paragraph.indexOf(k.sentence, from);
      if (at === -1) break;
      hits.push({ start: at, end: at + k.sentence.length, key });
      from = at + k.sentence.length;
    }
  });
  hits.sort((a, b) => a.start - b.start);
  const pieces: Piece[] = [];
  let pos = 0;
  for (const h of hits) {
    if (h.start < pos) continue;
    if (h.start > pos) pieces.push({ text: paragraph.slice(pos, h.start), key: null });
    pieces.push({ text: paragraph.slice(h.start, h.end), key: h.key });
    pos = h.end;
  }
  if (pos < paragraph.length) pieces.push({ text: paragraph.slice(pos), key: null });
  return pieces;
}
